import { FC, Fragment, ChangeEvent, memo } from 'react';
import { SearchCheckboxItem } from 'components/SearchCheckboxList';
import { JobCountQuery } from '@doda/common/src/services/getJobCountAPI';
import { SelectButton } from 'components/SearchSideBar/SelectButton';
import { useSelector } from 'react-redux';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { RouteStationDrillDown } from 'pages/DodaFront/View/JobSearchList/components/SearchSideBar/DrillDown/RouteStationDrillDown';
import { LocationDrillDown } from 'pages/DodaFront/View/JobSearchList/components/SearchSideBar/DrillDown/LocationDrillDown';
import { ConditionsAreaWrapper } from '../../ConditionsAreaWrapper';
import styles from './index.module.scss';
import { TitleWithButton } from '../TitleWithButton';
import { LocationType, RouteStationType } from '../types';

/**
 * Props
 * @param location 勤務地のチェックボックス表示用配列
 * @param routeStation 路線駅のチェックボックス表示用配列
 * @param updateJobCount 求人件数の更新処理
 * @param onClickOpenLocationModal 勤務地モーダルの表示処理
 * @param onClickOpenStationModal 路線駅モーダルの表示処理
 * @param onChangeIsShowOverlay オーバーレイの表示処理
 */
interface Props {
  location: LocationType;
  routeStation: RouteStationType;
  updateJobCount: (currentCondition?: JobCountQuery) => Promise<void>;
  onClickOpenLocationModal: () => void;
  onClickOpenStationModal: () => void;
  onChangeIsShowOverlay: () => void;
}

export const LocationArea: FC<Props> = memo(
  ({
    location,
    routeStation,
    updateJobCount,
    onClickOpenLocationModal,
    onClickOpenStationModal,
    onChangeIsShowOverlay,
  }) => {
    const { qc } = useSelector(appQueryParamsSelector);
    const {
      locationDisplayList,
      locationDrillDown,
      updateIsCheckedAreaList,
      updateIsCheckedPrefectureList,
      updateIsCheckedCityList,
      updateIsCheckedWardList,
      updateLocationDrillDownIsChecked,
    } = location;
    const {
      routeStationDisplayList,
      routeStationDrillDown,
      updateIsCheckedRouteList,
      updateIsCheckedStationList,
      updateStationDrillDownIsChecked,
    } = routeStation;

    // 地域のチェックボックス押下処理
    const onChangeAreaListCheckbox = (
      event: ChangeEvent<HTMLInputElement>,
      selectedId: string
    ) => {
      const checkedList = updateIsCheckedAreaList(event, selectedId);
      updateJobCount({ areaId: checkedList, qualificationId: qc });
      onChangeIsShowOverlay();
    };

    // 都道府県のチェックボックス押下処理
    const onChangePrefectureListCheckbox = (
      event: ChangeEvent<HTMLInputElement>,
      selectedId: string
    ) => {
      const checkedList = updateIsCheckedPrefectureList(event, selectedId);
      updateJobCount({ prefectureId: checkedList, qualificationId: qc });
      onChangeIsShowOverlay();
    };

    // 市区町村チェックボックス押下処理
    const onChangeCityListCheckbox = (
      event: ChangeEvent<HTMLInputElement>,
      selectedId: string
    ) => {
      const checkedList = updateIsCheckedCityList(event, selectedId);
      updateJobCount({ cityId: checkedList, qualificationId: qc });
      onChangeIsShowOverlay();
    };

    // 政令指定都市のチェックボックス押下処理
    const onChangeWardListCheckbox = (
      event: ChangeEvent<HTMLInputElement>,
      selectedId: string
    ) => {
      const checkedList = updateIsCheckedWardList(event, selectedId);
      updateJobCount({ wardId: checkedList, qualificationId: qc });
      onChangeIsShowOverlay();
    };

    // 路線のチェックボックス押下時の処理
    const onChangeRouteListCheckbox = (
      event: ChangeEvent<HTMLInputElement>,
      selectedId: string
    ) => {
      const checkedList = updateIsCheckedRouteList(event, selectedId);
      updateJobCount({ routeId: checkedList, qualificationId: qc });
      onChangeIsShowOverlay();
    };

    // 駅のチェックボックス押下時の処理
    const onChangeStationListCheckbox = (
      event: ChangeEvent<HTMLInputElement>,
      selectedId: string
    ) => {
      const checkedList = updateIsCheckedStationList(event, selectedId);
      updateJobCount({ stationId: checkedList, qualificationId: qc });
      onChangeIsShowOverlay();
    };

    // 駅のドリルダウンチェックボックス押下時の処理
    const onChangeStationDrillDown = (
      event: ChangeEvent<HTMLInputElement>,
      selectedId: string
    ) => {
      if (updateStationDrillDownIsChecked) {
        const checkedList = updateStationDrillDownIsChecked(event, selectedId);
        updateJobCount({
          routeId: checkedList?.routeId ?? [],
          stationId: checkedList?.stationId ?? [],
        });
        onChangeIsShowOverlay();
      }
    };

    // ドリルダウン（都道府県）のチェックボックス更新処理
    const onChangePrefectureDrillDown = (
      event: ChangeEvent<HTMLInputElement>,
      selectedId: string
    ) => {
      if (updateLocationDrillDownIsChecked) {
        const checkedList = updateLocationDrillDownIsChecked(event, selectedId);
        updateJobCount({
          areaId: checkedList?.areaId ?? [],
          prefectureId: checkedList?.prefectureId ?? [],
        });
        onChangeIsShowOverlay();
      }
    };

    // ドリルダウン（市区町村）のチェックボックス更新処理
    const onChangeCityDrillDown = (
      event: ChangeEvent<HTMLInputElement>,
      selectedId: string
    ) => {
      if (updateLocationDrillDownIsChecked) {
        const checkedList = updateLocationDrillDownIsChecked(event, selectedId);
        updateJobCount({
          prefectureId: checkedList?.prefectureId ?? [],
          cityId: checkedList?.cityId ?? [],
        });
        onChangeIsShowOverlay();
      }
    };

    // ドリルダウン（政令指定都市）のチェックボックス更新処理
    const onChangeWardDrillDown = (
      event: ChangeEvent<HTMLInputElement>,
      selectedId: string
    ) => {
      if (updateLocationDrillDownIsChecked) {
        const checkedList = updateLocationDrillDownIsChecked(event, selectedId);
        updateJobCount({
          cityId: checkedList?.cityId ?? [],
          wardId: checkedList?.wardId ?? [],
        });
        onChangeIsShowOverlay();
      }
    };

    // ドリルダウンのチェックボックス更新
    const onChangeLocationDrillDown = (
      event: ChangeEvent<HTMLInputElement>,
      selectedId: string,
      type: 'prefecture' | 'city' | 'ward'
    ) => {
      switch (type) {
        case 'prefecture':
          onChangePrefectureDrillDown(event, selectedId);
          break;
        case 'city':
          onChangeCityDrillDown(event, selectedId);
          break;
        case 'ward':
          onChangeWardDrillDown(event, selectedId);
          break;
        default:
          break;
      }
    };

    return (
      <ConditionsAreaWrapper>
        <TitleWithButton title="勤務地、路線・駅">
          <div className={styles['location-area__buttons']}>
            <SelectButton
              title="勤務地を選択"
              onClick={onClickOpenLocationModal}
            />
            <SelectButton
              title="路線・駅を選択"
              onClick={onClickOpenStationModal}
            />
          </div>
        </TitleWithButton>
        {locationDrillDown ? (
          <LocationDrillDown
            drillDown={locationDrillDown}
            onChange={(event, selectedId) =>
              onChangeLocationDrillDown(
                event,
                selectedId,
                locationDrillDown.type
              )
            }
          />
        ) : (
          <div className={styles['location-check-list']}>
            {locationDisplayList.map((area) => (
              <Fragment key={area.id}>
                {area.isDisplay && (
                  <SearchCheckboxItem
                    title={area.name}
                    isChecked={area.isChecked}
                    value={area.id}
                    count={area.jobCount}
                    onChange={(event) =>
                      onChangeAreaListCheckbox(event, area.id)
                    }
                  />
                )}
                {area.prefectureList.map((prefecture) => (
                  <Fragment key={prefecture.id}>
                    {prefecture.isDisplay && (
                      <SearchCheckboxItem
                        title={prefecture.name}
                        isChecked={prefecture.isChecked}
                        value={prefecture.id}
                        count={prefecture.jobCount}
                        onChange={(event) =>
                          onChangePrefectureListCheckbox(event, prefecture.id)
                        }
                      />
                    )}
                    {prefecture.cityList.map((city) => (
                      <Fragment key={city.id}>
                        {city.isDisplay && (
                          <SearchCheckboxItem
                            title={city.name}
                            isChecked={city.isChecked}
                            value={city.id}
                            count={city.jobCount}
                            onChange={(event) =>
                              onChangeCityListCheckbox(event, city.id)
                            }
                          />
                        )}
                        {city.wardList.map((ward) => (
                          <Fragment key={ward.id}>
                            {ward.isDisplay && (
                              <SearchCheckboxItem
                                title={ward.name}
                                isChecked={ward.isChecked}
                                value={ward.id}
                                count={ward.jobCount}
                                onChange={(event) =>
                                  onChangeWardListCheckbox(event, ward.id)
                                }
                              />
                            )}
                          </Fragment>
                        ))}
                      </Fragment>
                    ))}
                  </Fragment>
                ))}
              </Fragment>
            ))}
          </div>
        )}
        {routeStationDrillDown?.length ? (
          <RouteStationDrillDown
            routes={routeStationDrillDown ?? []}
            onChange={onChangeStationDrillDown}
          />
        ) : (
          <div className={styles['routeStation-check-list']}>
            {routeStationDisplayList.map((route, index) => (
              <Fragment key={index}>
                {route.isDisplay && (
                  <SearchCheckboxItem
                    title={route.name}
                    value={route.id}
                    isChecked={route.isChecked}
                    count={route.jobCount}
                    onChange={(event) =>
                      onChangeRouteListCheckbox(event, route.id)
                    }
                  />
                )}
                {route.stationList.map((station) => (
                  <Fragment key={station.id}>
                    {station.isDisplay && (
                      <SearchCheckboxItem
                        title={station.name}
                        value={station.id}
                        isChecked={station.isChecked}
                        count={station.jobCount}
                        onChange={(event) =>
                          onChangeStationListCheckbox(event, station.id)
                        }
                      />
                    )}
                  </Fragment>
                ))}
              </Fragment>
            ))}
          </div>
        )}
      </ConditionsAreaWrapper>
    );
  }
);

LocationArea.displayName = 'LocationArea';
